/**
 * App de Mac (ADR-G25): a página «App para Mac» (descarregar, instalar a versão de teste,
 * ligar e estado da ligação) e /mac/ligar, a página do PKCE que a app abre para ligar a conta.
 */
import { formatRelative } from '@nolens/i18n';
import { Banner, Button, useLocale } from '@nolens/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { macText } from '../i18n/mac';
import { shellText } from '../i18n/shell';
import { ApiError } from '../lib/api';
import { linkParams, macLinkUrl, openMac } from '../lib/mac';
import { WithMe } from './workspace';

const card = 'rounded-[14px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]';

export function MacAppPage() {
  const locale = useLocale();
  const t = macText(locale);
  const st = shellText(locale);
  return (
    <WithMe crumbs={() => [{ label: st.macApp }]}>
      {(me) => (
        <section className="max-w-xl" data-mac-page>
          <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-2 text-[15px] text-text-2">{t.hint}</p>

          <div className={`${card} mt-6 grid gap-3`}>
            {me.macAppUrl ? (
              <a href={me.macAppUrl} rel="noopener noreferrer" className="justify-self-start">
                <Button data-mac-download>{t.download}</Button>
              </a>
            ) : (
              <p className="text-text-2" data-mac-unavailable>
                {t.notAvailable}
              </p>
            )}
            <p className="text-sm text-text-3">{t.requirements}</p>
          </div>

          <div className="mt-6" data-mac-install>
            <h2 className="text-[15px] font-semibold">{t.testVersion}</h2>
            <p className="mt-1 text-[15px] text-text-2">{t.testVersionHint}</p>
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[15px] text-text-2">
              <li>{t.install1}</li>
              <li>{t.install2}</li>
              <li>{t.install3}</li>
            </ol>
            <p className="mt-3 text-sm leading-relaxed text-text-3">{t.permissions}</p>
          </div>

          <div
            className={`${card} mt-6 grid gap-3`}
            data-mac-connection={me.macApp ? 'linked' : 'none'}
          >
            <h2 className="text-[15px] font-semibold">{t.connection}</h2>
            {me.macApp ? (
              <>
                <p>{t.linked(me.macApp.version, formatRelative(locale, me.macApp.lastSeenAt))}</p>
                <Button
                  variant="secondary"
                  className="justify-self-start"
                  onClick={() => openMac('gravar')}
                >
                  {t.openApp}
                </Button>
              </>
            ) : (
              <>
                <p className="text-text-2">{t.notLinked}</p>
                <div>
                  <Button onClick={() => openMac('ligar')} data-mac-connect>
                    {t.connect}
                  </Button>
                  <p className="mt-2 text-sm text-text-3">{t.connectHint}</p>
                </div>
              </>
            )}
          </div>

          <p className="mt-6 text-sm leading-relaxed text-text-3">{t.whenExtension}</p>
          <p className="mt-6 text-sm text-text-3">
            <Link to="/inicio" className="underline">
              {t.backToGuides}
            </Link>
          </p>
        </section>
      )}
    </WithMe>
  );
}

/**
 * /mac/ligar?state=…&challenge=…: aberta pela app de Mac (ASWebAuthenticationSession). Pede
 * confirmação antes de criar o código: só a pessoa que acabou de carregar em «Ligar» na app
 * deve continuar (outra app no mesmo Mac podia abrir este endereço com o challenge dela).
 */
export function MacLinkPage() {
  const locale = useLocale();
  const t = macText(locale);
  const st = shellText(locale);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const params = linkParams(search);
  const link = useMutation({ mutationFn: () => macLinkUrl(params!) });
  // O endereço nolens-guides:// leva o código à app (a sessão de autenticação fecha-se sozinha)
  useEffect(() => {
    if (link.data) window.location.href = link.data;
  }, [link.data]);
  return (
    <WithMe crumbs={() => [{ label: st.macApp, to: '/mac' }, { label: t.linkTitle }]}>
      {(me) => (
        <section className="max-w-xl" data-mac-link>
          <h1 className="text-2xl font-semibold tracking-tight">{t.linkTitle}</h1>
          {!params ? (
            <Banner tone="critical" className="mt-4">
              {t.linkInvalid}
            </Banner>
          ) : link.data ? (
            <Banner tone="positive" className="mt-4">
              {t.linkDone}
            </Banner>
          ) : (
            <div className={`${card} mt-4 grid gap-4`}>
              <p className="text-[15px]">{t.linkAsk(me.user.email ?? '')}</p>
              <p className="text-sm text-text-2">{t.linkWarn}</p>
              {link.error && (
                <Banner tone="critical">
                  {link.error instanceof ApiError ? link.error.message : String(link.error)}
                </Banner>
              )}
              <div className="flex gap-2">
                <Button loading={link.isPending} onClick={() => link.mutate()} data-mac-confirm>
                  {t.linkConfirm}
                </Button>
                <Button variant="ghost" onClick={() => navigate('/inicio')}>
                  {t.cancel}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </WithMe>
  );
}
